<template>
  <div>
    <transition-group
      tag="ul"
      name="imsg"
      :css="false"
      :class="['icon-message-wrap old v2', { 'is-left': isOnLeftSide }]"
      @before-enter="handleBeforeEnter"
      @enter="handleEnter"
      @after-enter="updateIframeSize"
      @leave="handleLeave"
      @after-leave="updateIframeSize"
    >
      <icon-message-item
        v-for="(iconMessage, index) in iconMessages"
        :key="iconMessage.id"
        :index="index"
        :message="iconMessage"
        :toggle-chat="toggleChat"
        :data-icon-message-id="iconMessage.id"
        :data-index="index"
        :data-testid="`icon-message-item-${iconMessage.id}`"
        @close="handleCloseIconMessageByUser"
        @auto-hide="closeIconMessage"
        @image-loaded="onIconMessageImageLoaded"
      />
    </transition-group>

    <ul
      ref="icon-message-clone"
      :class="['icon-message-wrap old v2', { 'is-left': isOnLeftSide }]"
      :style="{
        right: '-999em',
        maxWidth: iframeMaxWidth + 'px',
        maxHeight: iframeMaxHeight + 'px'
      }"
    >
      <icon-message-item
        v-for="(iconMessage, index) in iconMessages"
        :key="iconMessage.id"
        :index="index"
        :toggle-chat="toggleChat"
        :message="iconMessage"
        clone
      />
    </ul>
  </div>
</template>

<script>
import IconMessageItem from './IconMessageItem'
import { appendClosedToLocalStorage } from 'Utilities/ClosedIconMessages'
import gsap from 'gsap'
import { useSettingsStore } from 'Store/settings'
import { useUserStore } from 'Store/user'
import { mapState } from 'pinia'
import { useIconMessageStore } from 'Store/icon-message'
import ResizeObserver from 'resize-observer-polyfill'
import { EventBus } from 'ChatWidget/EventBus'
import { computed } from 'vue'

const ANIMATION_DELAY = 200

export default {
  name: 'IconMessageWindow',

  components: {
    IconMessageItem
  },

  setup() {
    return {
      settingsStore: useSettingsStore(),
      iconMessageStore: useIconMessageStore(),
      isKnownUser: computed(() => useUserStore().isKnownUser)
    }
  },

  props: {
    toggleChat: Function
  },

  data() {
    return {
      currentWidth: 0,
      animationSetTimeoutId: null,
      iconMessageWidths: {},
      iconMessageWidthSizes: {},
      animationIndex: 0,
      animationLast: null,
      timeoutId: null
    }
  },

  computed: {
    ...mapState(useIconMessageStore, {
      iframeMaxHeight: 'iframeMaxHeight',
      iframeMaxWidthLeft: 'iframeMaxWidthLeft',
      iconMessages: 'displayedIconMessages'
    }),

    isOnLeftSide() {
      return this.settingsStore.iconMessagePosition === 'left'
    },

    iframeMaxWidth() {
      return this.isOnLeftSide ? this.iframeMaxWidthLeft : this.iconMessageStore.iframeMaxWidth
    }
  },

  watch: {
    isKnownUser() {
      this.$nextTick(() => {
        this.updateIframeSize()
      })
    },
    currentWidth(newValue, oldValue) {
      // sometimes transition event `after-enter` is emitted before the whole icon message item render completed
      // maybe because of heavy image loading, slow network,... so we can't completely rely on that event
      // then we use a watcher here to ensure that any size changes from the clone list of icon messages
      // will be reflected on the actual displayed one
      // this will solve almost of icon message size issues but still need to check event onload for images
      if (newValue > oldValue) {
        // new height > old height means new icon messages are appended to the list
        this.updateIframeSize()
        setTimeout(() => {
          this.updateIframeSize()
        }, 1000)
      } else {
        // we don't need to do this when an icon message is closed coz the above issue is not happened for
        // `after-leave` event so far and we have to update iframe size after-leave to make sure the transition is smooth
      }
    }
  },

  mounted() {
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.currentWidth = entry.contentRect.width
      }
    })

    setTimeout(() => {
      // https://app.rollbar.com/a/kaku-2/fix/item/FE-chatbot/12514
      ro.observe(this.$refs['icon-message-clone'])
    }, 0)

    this.$once('hook:beforeDestroy', () => {
      ro.disconnect()
    })

    EventBus.$on('change-locale', () => {
      this.$nextTick(() => {
        this.updateIframeSize()
      })
    })
  },

  methods: {
    onIconMessageImageLoaded() {
      // after an image loaded, invoke updateIframeSize to ensure we have correct iframe size
      // this case can be easier to reproduced by using slow 3G network on chrome devtools
      this.updateIframeSize()
    },

    closeIconMessage(iconMessage) {
      this.iconMessageStore.closeIconMessage(iconMessage)
    },

    handleCloseIconMessageByUser(iconMessage) {
      this.closeIconMessage(iconMessage)
      appendClosedToLocalStorage(iconMessage)
      this.$gtag.recordIconMessageHidden(iconMessage)
    },

    handleBeforeEnter() {
      this.iconMessageStore.resetIconMessageIframeSize()
    },

    handleEnter(el, done) {
      if (!this.animationLast || Date.now() - this.animationLast > ANIMATION_DELAY) {
        this.animationIndex = 0
      }

      const delay = (this.animationIndex * ANIMATION_DELAY) / 1000
      this.animationLast = Date.now()
      this.animationIndex += 1

      gsap.fromTo(
        el,
        { opacity: 0, translateY: 5 },
        { opacity: 1, translateY: 0, onComplete: done, duration: 0.4, delay }
      )
      setTimeout(() => {
        done() // Make sure the animation is complete before calling done
      }, 500)
    },

    handleLeave(el, done) {
      gsap.to(el, { opacity: 0, height: 0, duration: 0.4, onComplete: done })
      setTimeout(() => {
        done() // Make sure the animation is complete before calling done
      }, 500)
    },

    updateIframeSize() {
      const rect = this.$refs['icon-message-clone'].getBoundingClientRect()
      // +1px here seems solve a weird word break issue when the container width has changed?
      this.iconMessageStore.updateIframeWidth(rect.width + 1)
      this.iconMessageStore.updateIframeHeight(rect.height)
    }
  }
}
</script>
